import * as React from 'react';
import { type DragEvent, type KeyboardEvent, type RefObject, type SyntheticEvent, forwardRef } from 'react';

import { Image, styled } from '@volkswagen-onehub/components-core';
import { Checkmark } from '@volkswagen-onehub/icons-core';

import { KeyCode } from '../../utils/key-code';

const OPTION_BASE_SIZE = 46;

interface OptionsProps {
	ref: RefObject<HTMLButtonElement>;
	ariaLabel: string;
	ariaBusy: boolean;
	onClick: () => void;
	selectedOption?: boolean;
	altText: string;
	thumbnailUrl: string;
	testId: string;
}

export const Option = forwardRef<HTMLButtonElement, OptionsProps>(
	({ ariaLabel, ariaBusy, onClick, selectedOption, altText, thumbnailUrl, testId }, ref) => {
		function onDragStart(event: DragEvent<HTMLImageElement>) {
			event.preventDefault();
		}

		function onKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
			const events: Array<string> = [KeyCode.ENTER, KeyCode.SPACE];

			if (events.includes(event.key)) {
				onClick();
			}
		}

		function onOptionClick(event: SyntheticEvent) {
			event.preventDefault();
			event.stopPropagation();

			onClick();
		}

		return (
			<IconWrapper
				ref={ref}
				aria-busy={!selectedOption && !!ariaBusy}
				aria-current={!!selectedOption}
				aria-label={ariaLabel}
				tabIndex={0}
				onClick={onOptionClick}
				onKeyDown={onKeyDown}
				isActive={!!selectedOption}
				data-testid={testId}
			>
				<CheckmarkIcon variant={'small'} isActive={!!selectedOption} aria-hidden={true} />
				<ImageWrapper>
					<Image src={thumbnailUrl} alt={''} key={altText} draggable={false} onDragStart={onDragStart} />
				</ImageWrapper>
			</IconWrapper>
		);
	}
);

Option.displayName = 'Visualizer_Option';

const CheckmarkIcon = styled(Checkmark)<{ isActive: boolean }>`
	display: ${props => (props.isActive ? 'block' : 'none')};
	position: absolute;
	top: -6px;
	inset-inline-end: -6px;
	z-index: 1;
	width: 24px;
	height: 24px;
	padding: 7px;
	color: ${props => props.theme.colors.background.primary};
	background-color: #997f67;
	border-radius: 50%;
`;

const IconWrapper = styled.button<{ isActive: boolean }>`
	// Prevents chips from being highlighted on drag
	user-select: none;
	position: relative;
	box-sizing: border-box;
	width: 48px;
	height: 48px;
	padding: 1px;
	border: none;
	border-radius: 50%;
	cursor: ${({ isActive }) => (isActive ? 'default' : 'pointer')};
	background-color: transparent;

	&::after {
		content: '';
		position: absolute;
		border-radius: 50%;
		pointer-events: none;
		inset: ${({ isActive }) => (isActive ? '-1px' : '1px')};
		border: ${({ isActive }) => (isActive ? '3px solid #997f67' : '1px solid #949494')};
	}

	${({ isActive }) =>
		!isActive &&
		`&:hover::after {
			inset: 0;
			border: 2px solid #997f67;
			box-shadow: 0 2px 4px 0 rgba(49, 25, 2, 0.6);
		}`}

	&:focus-visible {
		outline: 2px solid #c86c03;
		outline-offset: -1px;
	}
`;

const ImageWrapper = styled.div`
	overflow: hidden;
	background-color: #fff;
	height: ${OPTION_BASE_SIZE}px;
	width: ${OPTION_BASE_SIZE}px;
	border-radius: 50%;
`;
